import React, { useEffect, useState } from "react";
import { LogOut, Loader2, Edit3 } from "lucide-react";
import { toast } from "sonner";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../lib/api";

const initialsOf = (user) => {
  const f = (user?.first_name || "").trim();
  const l = (user?.last_name || "").trim();
  if (f || l) return `${f.charAt(0)}${l.charAt(0)}`.toUpperCase();
  return (user?.email || "S").charAt(0).toUpperCase();
};

const fmtMonth = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "long", year: "numeric" });
  } catch (_) {
    return null;
  }
};

const StatBlock = ({ value, label, testId }) => (
  <div
    data-testid={testId}
    className="flex flex-col items-center justify-center gap-2 px-6 py-8 bg-napoli-espresso"
  >
    <span className="font-display font-bold text-[32px] sm:text-[36px] text-napoli-gold leading-none text-center">
      {value}
    </span>
    <span className="mt-2 font-body font-semibold text-[10px] tracking-[0.3em] uppercase text-napoli-cream/80 text-center">
      {label}
    </span>
  </div>
);

const StatsCard = ({ stats, loading }) => {
  const sinceLabel = stats ? fmtMonth(stats.shifts_active_since) : null;
  return (
    <section
      data-testid="staff-profile-stats-card"
      className="mt-10 bg-napoli-marble border border-napoli-gold/30 px-5 sm:px-7 py-6"
    >
      <p className="font-body font-semibold text-[10px] tracking-[0.3em] uppercase text-napoli-oxblood">
        Your impact
      </p>
      {loading ? (
        <div className="mt-5 flex items-center justify-center py-8">
          <Loader2 size={18} className="animate-spin text-napoli-oxblood" />
        </div>
      ) : !stats ? (
        <p className="mt-4 font-body italic text-[13px] text-napoli-espresso-soft">
          Stats unavailable right now.
        </p>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="staff-profile-stats-grid">
            <StatBlock testId="stats-points-awarded" value={`${Number(stats.total_points_awarded || 0).toLocaleString()} pts`} label="Awarded to customers" />
            <StatBlock testId="stats-customers-served" value={Number(stats.total_customers_served || 0).toLocaleString()} label="Customers served" />
            <StatBlock testId="stats-redemptions" value={Number(stats.total_redemptions_processed || 0).toLocaleString()} label="Rewards redeemed" />
          </div>
          {sinceLabel ? (
            <p data-testid="stats-active-since" className="mt-4 font-body italic text-[12px] text-napoli-espresso-soft text-center">
              Active since {sinceLabel}
            </p>
          ) : null}
        </>
      )}
    </section>
  );
};

const StaffEditPhoneModal = ({ open, onClose, currentPhone, onSaved }) => {
  const [phone, setPhone] = useState(currentPhone || "");
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { if (open) { setPhone(currentPhone || ""); setSubmitting(false); } }, [open, currentPhone]);
  const valid = !phone || isValidPhoneNumber(phone || "");
  const submit = async () => {
    if (!valid) return;
    setSubmitting(true);
    try {
      const { data } = await api.patch("/staff/me", { phone: phone || "" });
      toast.success("Phone updated.");
      onSaved?.(data);
      onClose();
    } catch (e) {
      const code = e?.response?.data?.detail?.error;
      toast.error(code === "phone_taken" ? "Phone already in use." : "Couldn't update phone.");
    } finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="staff-edit-phone-modal" className="!bg-napoli-marble !border-napoli-gold/30 !rounded-none !text-napoli-espresso !max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-[24px] text-left">Edit phone</DialogTitle>
          <DialogDescription className="font-body text-[13px] text-left text-napoli-espresso-soft">SMS receipts and 2FA.</DialogDescription>
        </DialogHeader>
        <div className="napoli-phone-wrapper bg-napoli-cream-warm border border-napoli-gold/40 focus-within:border-napoli-oxblood">
          <PhoneInput international defaultCountry="US" value={phone} onChange={(v) => setPhone(v || "")} data-testid="staff-edit-phone-input" />
        </div>
        <div className="flex gap-3 pt-3">
          <button type="button" data-testid="staff-edit-phone-cancel" onClick={onClose} className="flex-1 border border-napoli-espresso/40 py-3 font-body font-semibold text-[12px] tracking-[0.22em] uppercase hover:bg-napoli-cream-warm">Cancel</button>
          <button type="button" data-testid="staff-edit-phone-save" disabled={!valid || submitting} onClick={submit} className="flex-1 bg-napoli-oxblood text-napoli-cream py-3 font-body font-semibold text-[12px] tracking-[0.22em] uppercase hover:bg-napoli-oxblood-dark disabled:opacity-50">{submitting ? "…" : "Save"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const StaffEditPrefsModal = ({ open, onClose, current, onSaved }) => {
  const [optIn, setOptIn] = useState(!!current);
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { if (open) { setOptIn(!!current); setSubmitting(false); } }, [open, current]);
  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.patch("/staff/me", { promotional_emails_opt_in: optIn });
      toast.success("Preferences updated.");
      onSaved?.(data);
      onClose();
    } catch (_) { toast.error("Couldn't update."); }
    finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="staff-edit-prefs-modal" className="!bg-napoli-marble !border-napoli-gold/30 !rounded-none !text-napoli-espresso !max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-[24px] text-left">Email preferences</DialogTitle>
          <DialogDescription className="font-body text-[13px] text-left text-napoli-espresso-soft">Transactional emails (PIN reset, etc.) always send.</DialogDescription>
        </DialogHeader>
        <label htmlFor="staff-prefs-opt-in" className="flex items-start gap-3 cursor-pointer bg-napoli-cream-warm/60 border border-napoli-gold/25 px-4 py-3">
          <input id="staff-prefs-opt-in" data-testid="staff-edit-prefs-input" type="checkbox" checked={optIn} onChange={(e) => setOptIn(e.target.checked)} className="mt-0.5 h-4 w-4 accent-napoli-oxblood" />
          <span className="font-body text-[13px] leading-snug text-napoli-espresso">
            Send me occasional team updates from Napoli.
          </span>
        </label>
        <div className="flex gap-3 pt-3">
          <button type="button" data-testid="staff-edit-prefs-cancel" onClick={onClose} className="flex-1 border border-napoli-espresso/40 py-3 font-body font-semibold text-[12px] tracking-[0.22em] uppercase hover:bg-napoli-cream-warm">Cancel</button>
          <button type="button" data-testid="staff-edit-prefs-save" disabled={submitting} onClick={submit} className="flex-1 bg-napoli-oxblood text-napoli-cream py-3 font-body font-semibold text-[12px] tracking-[0.22em] uppercase hover:bg-napoli-oxblood-dark disabled:opacity-50">{submitting ? "…" : "Save"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const StaffProfilePage = () => {
  const { user, logout, updateUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [editPhoneOpen, setEditPhoneOpen] = useState(false);
  const [editPrefsOpen, setEditPrefsOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/staff/me-stats");
        if (alive) setStats(data);
      } catch (_) { if (alive) setStats(null); }
      finally { if (alive) setStatsLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(" ") || "Staff";
  const memberSince = fmtMonth(user?.created_at) || "—";

  const onPatched = (resp) => {
    if (!resp || !user) return;
    updateUser({ ...user, phone: resp.phone ?? user.phone, promotional_emails_opt_in: resp.promotional_emails_opt_in ?? user.promotional_emails_opt_in });
  };

  return (
    <section data-testid="staff-profile-page" className="px-6 pt-10 pb-12 max-w-xl mx-auto">
      <div className="flex flex-col items-center text-center">
        <div aria-hidden className="h-20 w-20 rounded-full bg-napoli-oxblood text-napoli-cream flex items-center justify-center font-display font-bold text-[26px]">
          {initialsOf(user)}
        </div>
        <h1 className="mt-5 font-display font-bold text-[24px] text-napoli-cream">{fullName}</h1>
        <span data-testid="staff-profile-role-badge" className="mt-3 inline-flex items-center gap-2 px-3 py-1 border border-napoli-gold/40 text-napoli-gold font-body text-[10px] tracking-[0.22em] uppercase">
          <span className="w-1.5 h-1.5 rounded-full bg-napoli-gold" aria-hidden />
          Staff
        </span>
      </div>

      <StatsCard stats={stats} loading={statsLoading} />

      <dl className="mt-8 bg-napoli-espresso-soft border border-napoli-gold/20 divide-y divide-napoli-gold/15">
        <div className="px-5 py-4 flex items-center justify-between">
          <dt className="font-body text-[10px] tracking-[0.22em] uppercase text-napoli-cream/60">Email</dt>
          <dd data-testid="staff-profile-email" className="font-body text-[13px] text-napoli-cream">{user?.email || "—"}</dd>
        </div>
        <div className="px-5 py-4 flex items-center justify-between gap-3">
          <dt className="font-body text-[10px] tracking-[0.22em] uppercase text-napoli-cream/60">Phone</dt>
          <div className="flex items-center gap-3">
            <dd data-testid="staff-profile-phone" className="font-body text-[13px] text-napoli-cream">{user?.phone || "—"}</dd>
            <button type="button" data-testid="staff-profile-edit-phone" onClick={() => setEditPhoneOpen(true)} className="text-napoli-gold hover:text-napoli-cream" aria-label="Edit phone">
              <Edit3 size={14} />
            </button>
          </div>
        </div>
        <div className="px-5 py-4 flex items-center justify-between gap-3">
          <dt className="font-body text-[10px] tracking-[0.22em] uppercase text-napoli-cream/60">Email preferences</dt>
          <div className="flex items-center gap-3">
            <dd data-testid="staff-profile-prefs" className="font-body text-[13px] text-napoli-cream">{user?.promotional_emails_opt_in ? "Promo on" : "Promo off"}</dd>
            <button type="button" data-testid="staff-profile-edit-prefs" onClick={() => setEditPrefsOpen(true)} className="text-napoli-gold hover:text-napoli-cream" aria-label="Edit preferences">
              <Edit3 size={14} />
            </button>
          </div>
        </div>
        <div className="px-5 py-4 flex items-center justify-between">
          <dt className="font-body text-[10px] tracking-[0.22em] uppercase text-napoli-cream/60">PIN</dt>
          <dd className="font-body italic text-[12px] text-napoli-cream/70">
            Forgot PIN? Contact your manager.
          </dd>
        </div>
        <div className="px-5 py-4 flex items-center justify-between">
          <dt className="font-body text-[10px] tracking-[0.22em] uppercase text-napoli-cream/60">Member since</dt>
          <dd className="font-body text-[13px] text-napoli-cream">{memberSince}</dd>
        </div>
      </dl>

      <button type="button" data-testid="staff-profile-logout" onClick={() => logout("/")} className="mt-10 w-full inline-flex items-center justify-center gap-2 bg-napoli-oxblood text-napoli-cream font-body font-semibold text-[12px] tracking-[0.22em] uppercase py-4 hover:bg-napoli-oxblood-dark transition-colors">
        <LogOut size={16} />
        Log out
      </button>

      <StaffEditPhoneModal open={editPhoneOpen} onClose={() => setEditPhoneOpen(false)} currentPhone={user?.phone} onSaved={onPatched} />
      <StaffEditPrefsModal open={editPrefsOpen} onClose={() => setEditPrefsOpen(false)} current={!!user?.promotional_emails_opt_in} onSaved={onPatched} />
    </section>
  );
};

export default StaffProfilePage;
